import storage from 'local-storage-fallback';
import { CRField, PatchData } from '@smart-builder/types';
import { Field, FieldContainer } from './types';

export function helpers<X extends {}, Y extends PropertyKey>(
  obj: X,
  prop: Y,
): obj is X & Record<Y, unknown> {
  return obj.hasOwnProperty(prop);
}

export const DEFAULT_GLASS_COLOUR_BGR = 0xd0fdd7;
export const DEFAULT_GLASS_OPACITY = 0.3;

type RGBColour = {
  r: number;
  g: number;
  b: number;
};

export function getOrdinalColour(ordinal: number): RGBColour {
  ordinal = ordinal % 15;
  switch (ordinal) {
    case 0:
      return { r: 200, g: 10, b: 90 };
    case 1:
      return { r: 40, g: 40, b: 220 };
    case 2:
      return { r: 40, g: 180, b: 40 };
    case 3:
      return { r: 254, g: 141, b: 40 };
    case 4:
      return { r: 160, g: 0, b: 130 };
    case 5:
      return { r: 0, g: 125, b: 125 };
    case 6:
      return { r: 125, g: 0, b: 0 };
    case 7:
      return { r: 0, g: 0, b: 100 };
    case 8:
      return { r: 200, g: 40, b: 40 };
    case 9:
      return { r: 40, g: 180, b: 40 };
    case 10:
      return { r: 40, g: 40, b: 220 };
    case 11:
      return { r: 130, g: 0, b: 160 };
    case 12:
      return { r: 123, g: 125, b: 125 };
    case 13:
      return { r: 40, g: 100, b: 0 };
    case 14:
      return { r: 200, g: 200, b: 40 };
    default:
      return { r: 0, g: 0, b: 0 };
  }
}

export function rgbToInt(r: number, g: number, b: number): number {
  return (r << 16) | (g << 8) | b;
}

export function rgbToHex(rgb: number): string {
  return `#${rgb.toString(16).padStart(6, '0')}`;
}

export function bgrToRgbHex(bgr: number): string {
  return rgbToHex(bgrToRgb(bgr));
}

export function bgrToRgb(bgr: number): number {
  const r = bgr & 0x0000ff;
  const g = (bgr & 0x00ff00) >> 8;
  const b = (bgr & 0xff0000) >> 16;
  return rgbToInt(r, g, b);
}
// function is an involution
export const rgbToBgr = bgrToRgb;

export const camelize = (str: string) => {
  return str.replace(
    /(?:^\w|[A-Z]|\b\w|\s+)/g,
    (match: string, index: number) => {
      if (+match === 0) return ''; // or if (/\s+/.test(match)) for white spaces
      return index === 0 ? match.toLowerCase() : match.toUpperCase();
    },
  );
};

const simplifyURL = (url: string): string => {
  try {
    return new URL(url).toString();
  } catch {
    // if the url is invalid, return original url
    return url;
  }
};

export const concatUrl = (url1: string, url2: string): string => {
  const hasTrailing = url1.endsWith('/');
  const hasLeading = url2.startsWith('/');
  if (hasTrailing && hasLeading) {
    return simplifyURL(url1 + url2.substr(1));
  } else if (hasTrailing || hasLeading || !url1 || !url2) {
    return simplifyURL(url1 + url2);
  } else {
    return simplifyURL(url1 + '/' + url2);
  }
};

export const makeReadible = (str: string) => {
  return str.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export function hasOwnProperty<X extends {}, Y extends PropertyKey>(
  obj: X,
  prop: Y,
): obj is X & Record<Y, unknown> {
  return obj.hasOwnProperty(prop);
}

export function stripFromString(value?: string, stripCharacter?: string) {
  if (!value || !stripCharacter) {
    return value;
  } else {
    let result = value;
    let index = value.indexOf(stripCharacter);
    while (index >= 0) {
      result =
        value.substr(0, index) + value.substr(index + stripCharacter.length);
      index = result.indexOf(stripCharacter);
    }
    return result;
  }
}

enum parseState {
  pre,
  integer,
  decimal,
  betweenIntAndFrac,
  numerator,
  denominator,
  end,
}

export const parseLength = (
  length: string | number | undefined,
  allowLengthF?: boolean,
  setError?: (error: string) => void,
): number => {
  if (length === undefined) {
    return 0;
  }

  // T11121 SS 20220524 It is possible that length is already a number, which can/will be the case when using metric.
  // If length is already a valid number then this function returns NaN due to it converting the result of parseFloat
  // back to a string and then comparing it against the original value, which fails as the original value is not a string..
  // Seems to have been done this way due to parseFloat allowing values that are otherwise invalid
  // e.g. '1.2aaaa&' will be parsed to 1.2.
  if (typeof length === 'number') {
    return length;
  }

  if (SB_CONFIG.isImperial && !length?.includes('.')) {
    let multiplier = 1,
      whole = 0,
      decimal = 0,
      numerator = 0,
      denominator = 1;
    let currStr = '';
    let state = parseState.pre;
    const trimedStr = length.trim();
    for (let char of trimedStr + (trimedStr.endsWith('"') ? '' : '"')) {
      // Force a " at the end
      if (state === parseState.end) {
        setError && setError('Cannot have chars after the ".');
        return NaN;
      } else if (char === '-') {
        if (state === parseState.pre) {
          if (multiplier === -1) {
            setError &&
              setError('Multiple minus signs at the start of length value.');
            return NaN;
          }
          multiplier = multiplier * -1;
        } else if (state === parseState.integer) {
          whole = parseInt(currStr);
          currStr = '';
          state = parseState.betweenIntAndFrac;
        } else if (state === parseState.betweenIntAndFrac) {
          // Do nothing
        } else {
          setError && setError('Unexpected dash in length value.');
          return NaN; // Bad format
        }
      } else if (char === '/') {
        if (state === parseState.numerator || state === parseState.integer) {
          numerator = parseInt(currStr);
          currStr = '';
          state = parseState.denominator;
        } else {
          setError && setError('Unexpected slash in length value.');
          return NaN; // Bad format
        }
      } else if (char === ' ') {
        if (state === parseState.integer) {
          whole = parseInt(currStr);
          currStr = '';
          state = parseState.betweenIntAndFrac;
        } else if (state === parseState.betweenIntAndFrac) {
          // Do nothing
        } else if (state === parseState.pre) {
          // Do nothing
        } else {
          setError && setError('Unexpected space in length value.');
          return NaN; // Bad format
        }
      } else if (char === '"') {
        if (state === parseState.integer) {
          whole = parseInt(currStr);
        } else if (state === parseState.decimal) {
          decimal = parseInt(currStr) / Math.pow(10, currStr.length);
        } else if (state === parseState.denominator) {
          denominator = parseInt(currStr);
          const validDenominators = [
            2,
            4,
            8,
            16,
            ...(allowLengthF ? [32, 64] : []),
          ];
          if (validDenominators.indexOf(denominator) < 0) {
            setError &&
              setError(
                'Denominator of fraction must be one of the following: ' +
                  validDenominators.join(', ') +
                  '.',
              );
            return NaN; // Bad format
          }
        } else {
          setError && setError('Incomplete length value.');
          return NaN; // Bad format
        }
        state = parseState.end;
      } else if (char === '.') {
        if (state === parseState.pre) {
          state = parseState.decimal;
        } else if (state === parseState.integer) {
          whole = parseInt(currStr);
          currStr = '';
          state = parseState.decimal;
        } else {
          setError &&
            setError(
              'Cannot combine decimal with fraction notation for length values.',
            );
          return NaN; // Bad format
        }
      } else if (char >= '0' && char <= '9') {
        if (state === parseState.pre) {
          state = parseState.integer;
        } else if (state === parseState.betweenIntAndFrac) {
          state = parseState.numerator;
        }
        currStr = currStr + char;
      } else {
        setError &&
          setError('Unsupported non-numeric character in length value.');
        return NaN; // Bad format
      }
    }
    if (state === parseState.end) {
      return multiplier * (whole + decimal + numerator / denominator);
    } else {
      setError && setError('Unknown error.');
      return NaN; // Shouldn't be able to reach here.
    }
  } else {
    const formattedLength = length.startsWith('.') ? `0${length}` : length; // prefix the 0 on if needed
    const result = parseFloat(formattedLength);

    if (
      isNaN(result) ||
      // parseFloat appears to be very forgiving of invalid input. More-so
      // than our server.
      result + '' !== formattedLength
    ) {
      setError && setError('Invalid decimal value');
      return NaN;
    } else {
      return result;
    }
  }
};

export const lengthFormat = (value?: number, includeUnits: boolean = true) => {
  if (value === undefined) {
    return '';
  } else if (SB_CONFIG.isImperial) {
    const absValue = Math.abs(value);
    const sign = value >= 0 ? '' : '-';

    // TODO assuming 16ths
    // Also TODO - not supporting sub-denominations yet
    const units = 16;

    const inches = Math.floor(absValue / units);
    let numerator = value % units;
    let denominator = units;
    if (numerator > 0) {
      while (numerator % 2 === 0) {
        numerator = numerator / 2;
        denominator = denominator / 2;
      }
    }
    return (
      sign +
      (value
        ? concatStrings('-', [
            inches > 0 ? inches + '' : '',
            numerator > 0 ? numerator + '/' + denominator : '',
          ])
        : '0') +
      '"'
    ); // Always include units regardless?
  } else {
    return value + (includeUnits ? 'mm' : '');
  }
};

// This is also known as 'lower' camelCase, however it appears fairly common to just refer to it as camelCase.
export const makeCamelCase = (name: string) =>
  name.charAt(0).toLowerCase() + name.substr(1);

export const mapCamelCase = (names: string[]) =>
  names.map(name => makeCamelCase(name));

// This is also known as 'upper' camelCase.
export const makePascalCase = (name: string) =>
  name.charAt(0).toUpperCase() + name.substr(1);

export const stripFieldName = (fieldName: string) => {
  return fieldName.substr(fieldName.lastIndexOf('/') + 1);
};

export const imageUrl = (src?: string) => {
  if (src?.startsWith('data:')) {
    return src;
  } else {
    return concatUrl(SB_CONFIG.serverURL, src || '');
  }
};

export const getAbsoluteSrc = (src?: string) => {
  if (
    !src ||
    src.startsWith('http://') ||
    src.startsWith('https://') ||
    src.startsWith('data:') ||
    src.startsWith(SB_CONFIG.serverURL)
  ) {
    // this is already an absolute path
    return src;
  } else {
    return concatUrl(SB_CONFIG.serverURL, src);
  }
};

export const formatValue = (fieldContainer?: FieldContainer, field?: Field) => {
  if (fieldContainer) {
    let value;
    if (field) {
      const { name, format } = field;
      if (name) {
        value = fieldContainer[name];
      }
      if (format) {
        return format(value);
      }
      return value;
    }
  }
  return null;
};

// Only a simple compare. Do not use this if it's arrays of objects.
export const arrayCompare = (array1?: any[], array2?: any[]) => {
  if (array1 && array2) {
    if (array1.length === array2.length) {
      return array1.every((value, index) => array2[index] === value);
    } else {
      return false;
    }
  } else if (!array1 && !array2) {
    // Both are null/undefined. Treat them the same.
    return true;
  } else {
    // One is empty/undefined. Other one isn't.
    return false;
  }
};

export const isNullOrUndefined = (value: any) =>
  value === null || value === undefined;

export const isNullOrEmptyStr = (value?: any, allowWhiteSpace?: boolean) =>
  isNullOrUndefined(value) ||
  (typeof value === 'string' &&
    (allowWhiteSpace ? value : value?.trim()) === '');

export const isNullOrEmptyArray = (value?: any[]) => (value?.length ?? 0) === 0;

export const arrayContains = (lookupValue?: any, array?: any[]) =>
  !isNullOrEmptyArray(array) &&
  array!.some(arrayValue => arrayValue === lookupValue);

export function openExternalURL(
  url: string,
  autoConcatServerUrl: boolean = true,
) {
  let absoluteURL = url;
  if (
    autoConcatServerUrl &&
    !(url.startsWith('http://') || url.startsWith('https://'))
  ) {
    absoluteURL = concatUrl(SB_CONFIG.serverURL, url);
  }
  window.open(absoluteURL, '_blank');
}

export function equalsOrStartsWith(
  value: string,
  substr: string,
  separator: string = '/',
) {
  return value === substr || value.startsWith(substr + separator);
}

// Deep match, iterating through arrays and objects.
// Matches undefined with undefined on objects. If key isn't included, it's treated as undefined.
export const objectsMatch = (obj1: any, obj2: any) => {
  if (obj1 === undefined && obj2 === undefined) {
    return true;
  } else if (obj1 === null && obj2 === null) {
    return true;
  } else {
    const obj1Type = typeof obj1;
    const obj2Type = typeof obj2;
    if (obj1Type === obj2Type) {
      if (obj1Type === 'object') {
        if (Array.isArray(obj1) === Array.isArray(obj2)) {
          if (Array.isArray(obj1)) {
            if (obj1.length === obj2.length) {
              for (let i = 0; i < obj1.length; i++) {
                if (!objectsMatch(obj1[i], obj2[i])) {
                  return false;
                }
              }
              return true;
            } else {
              return false;
            }
          } else {
            const keys = [
              ...new Set([...Object.keys(obj1), ...Object.keys(obj2)]),
            ];
            for (let i = 0; i < keys.length; i++) {
              if (!objectsMatch(obj1[keys[i]], obj2[keys[i]])) {
                return false;
              }
            }
            return true;
          }
        } else {
          return false;
        }
      } else {
        return obj1 === obj2;
      }
    } else {
      return false;
    }
  }
};

export const concatStrings = (
  seperator: string,
  strings: (string | undefined)[],
  trim: boolean = true,
  excludeEmpty: boolean = true,
) => {
  return (
    strings.reduce((prev, string) => {
      const nextString = (trim ? string?.trim() : string) ?? '';
      if (nextString || !excludeEmpty) {
        if (prev) {
          return prev + seperator + nextString;
        } else {
          return nextString;
        }
      } else {
        return prev;
      }
    }, '') ?? ''
  );
};

export const insertSpaces = (text: string): string => {
  let result = text
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, '$1 $2');
  result.length > 0 && (result = result[0].toUpperCase() + result.substring(1));
  return result;
};

export const findPatchDataForFieldName = (
  patchData: PatchData,
  fieldName: string,
) => patchData.find(patch => patch.fieldName === fieldName);

export const extractKeyFromIdentifier = (identifier?: string) => {
  if (identifier) {
    const parts = identifier.split('/');
    return parts[parts.length - 1];
  } else {
    return undefined;
  }
};

export const extractKeyFromPatchData = (
  patchData: PatchData | undefined,
  fieldName: string,
) =>
  patchData &&
  extractKeyFromIdentifier(
    findPatchDataForFieldName(patchData, fieldName)?.result,
  );

export const fieldsIncludesTerms = (
  terms: string[],
  fields: (string | undefined)[],
) => {
  const cleanFields = fields.map(field => field?.toLowerCase());

  for (const term of terms) {
    if (!cleanFields.some(field => field?.includes(term))) {
      return false;
    }
  }
  return true;
};

export const clampValueToRange = (
  oldVal: number,
  oldMax: number,
  oldMin: number,
  newMax: number,
  newMin: number,
): number => {
  const oldRange = oldMax - oldMin;
  const newRange = newMax - newMin;
  if (oldRange === 0) {
    return 0;
  }
  return ((oldVal - oldMin) * newRange) / oldRange + newMin;
};

// @ts-ignore
window.SBHelpers = { ...window.SBHelpers, fieldsIncludesTerms };

// for removing undefined elements from an array / collection and getting Typescript to update the types properly
//    see https://stackoverflow.com/a/57989288
export const isDefined = <S>(value: S | undefined): value is S =>
  value !== undefined;

export const toggleNoScroll = (noScroll: boolean) => {
  if (noScroll) {
    document.body.classList.add('no-scroll');
  } else {
    document.body.classList.remove('no-scroll');
  }
};

export const FindOptionTitle = (PField?: CRField, PClient?: boolean) => {
  const value = PClient ? PField?.client.value : PField?.server.value;
  return PField?.options?.find(({ identifier }) => identifier === value)?.title;
};

export function queryXtiId() {
  const queryParams = new URLSearchParams(document.location.search);
  const queryXtiId = queryParams.get('xti') || undefined;
  return queryXtiId ? parseInt(queryXtiId) : undefined;
}

function storageKey(url?: string) {
  let key =
    url ?? new URL(window.location.pathname, window.location.origin).href;
  if (!key.endsWith('/')) {
    key += '/';
  }

  // if an xti is specified in the query params, it means this path can be used for multiple XTIs,
  // hence must include the XTI
  const xti = queryXtiId();
  if (xti) {
    key += xti + '/';
  }

  return key.toLowerCase();
}

export const SBStorage = {
  getItem: (key: string) => storage.getItem(storageKey() + key),
  setItem: (key: string, value: string, forUrl?: string) =>
    storage.setItem(storageKey(forUrl) + key, value),
  removeItem: (key: string, forUrl?: string) =>
    storage.removeItem(storageKey(forUrl) + key),
};

export function xor(a: boolean, b: boolean): boolean {
  return !!(Number(a) ^ Number(b));
}

export function timestampToDate(
  unixTimestamp?: string | number,
  isUTC?: boolean,
): Date | undefined {
  if (unixTimestamp === undefined) {
    return undefined;
  } else {
    // TODO what if the timezone offset changes between the 2?
    const timezoneOffset = isUTC
      ? 0
      : new Date(+unixTimestamp).getTimezoneOffset() * 60;
    return new Date((+unixTimestamp + timezoneOffset) * 1000);
  }
}

export const appIsPlusToolbox = () => SB_CONFIG.appName === 'plus-toolbox';
